/**
 * The signed-in Home, laid out like the app's Home scene: a side menu
 * (Projects / Folders / Classrooms / Settings) and a content pane routed by
 * the URL hash (see router.ts).
 *
 * Data: users/{uid} (folders), users/{uid}/projects (summaries; the ones with
 * `deletedAt` are in the bin, "Recently Deleted", and nowhere else) and
 * projects/{id} (full project, for the project page), all live from
 * Firestore; mutations go through the manageProject / manageFolder functions.
 * On sign-in the legacy projects are synced once (syncMyProjects).
 */
import { lazy, Suspense, useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from "react";
import { playLink, playProjectLink } from "../site";
import { uiDir } from "../player/rtl";
import type { Project } from "@makeit/schema";
import type { ProjectSource } from "../data/loadProject";
import { libraryUrl } from "../data/media";
import { AuthError, changeName, changePassword, deleteAccount, signOut, type AuthUser } from "../data/auth";
import {
  saveProjectsOrder,
  type ProjectsOrder,
  addDemos,
  createClassroom,
  createFolder,
  createProject,
  addProjectsToFolder,
  deleteFolder,
  forgetShared,
  redeemCode,
  watchClassrooms,
  watchShared,
  type Classroom,
  binDaysLeft,
  deleteProject,
  duplicateProject,
  purgeProject,
  restoreProject,
  renameFolder,
  renameProject,
  screenshotUrl,
  setProjectFolders,
  shareProject,
  syncMyProjects,
  thumbUrl,
  watchAccount,
  watchMyProjects,
  watchProject,
  type Account,
  type Folder,
  type ProjectKind,
  type ProjectMeta,
  type ProjectSummary,
  type SyncProgress,
} from "../data/myProjects";
import { currentLanguage, t } from "../player/i18n";
import { AppleButton, COLOR_CHOICES, ConfirmDialog, Icon, Modal, MoreMenu, PromptDialog, SearchBox, TEMPLATE_CHOICES, TypeBadge, isQuizType, matchesSearch, templateArt, templateBanner } from "./widgets";
import { hrefOf, navigate, sameRoute, useRoute, type Route } from "./router";
import { slideTypeName } from "./slideNames";
import { MyAnswersReport, RankingReport, StatsReport } from "./Reports";
import { loadLocalAnswers } from "../data/answers";
import { ClassroomPage, ProjectClassrooms, StudentView } from "./Classrooms";
import { ProjectCardPicker } from "./ProjectCardPicker";
import { SettingsView } from "./SettingsView";
import { SubscriptionView, subText } from "./SubscriptionView";
import { FreeLimitsContext, LIMIT_MESSAGE, useFreeLimits, type FreeLimits } from "./freeLimits";
import { AppAlert } from "./AppAlert";
import { loadAppTexts } from "../editor/appText";
import { canCreate, isPremium, limitOfError, managesSchool, refreshSubscription, type LimitKind } from "../data/subscription";
import { SchoolView } from "./SchoolView";
import { st } from "./schoolText";
import { HelpView } from "./HelpView";


/** Pages without a stored screenshot (edited on the web) are drawn live, as in the editor. */
const LivePreview = lazy(async () => {
  const [{ MiniSlide }, { loadAppTexts }] = await Promise.all([import("../editor/Workspace"), import("../editor/appText"), import("../data/media").then((m) => m.ensureLibraryManifest())]);
  await loadAppTexts();
  return { default: MiniSlide };
});

interface HomeData {
  user: AuthUser;
  /** the user's projects (not in the bin) */
  projects: ProjectSummary[] | null;
  /** the ones in the bin */
  binned: ProjectSummary[] | null;
  shared: ProjectSummary[] | null;
  classrooms: Classroom[];
  account: Account | null;
  sync: { state: "idle" | "running" | "done" | "error"; progress?: SyncProgress };
  resync: () => void;
}

export function HomeApp({ user, onPlay }: { user: AuthUser; onPlay: (source: ProjectSource) => void }) {
  const route = useRoute();
  const [allProjects, setAllProjects] = useState<ProjectSummary[] | null>(null);
  const projects = useMemo(() => allProjects && allProjects.filter((p) => !p.deletedAt), [allProjects]);
  const binned = useMemo(() => allProjects && allProjects.filter((p) => !!p.deletedAt), [allProjects]);
  const [shared, setShared] = useState<ProjectSummary[] | null>(null);
  const [classrooms, setClassrooms] = useState<Classroom[]>([]);
  const [account, setAccount] = useState<Account | null>(null);
  const [sync, setSync] = useState<HomeData["sync"]>({ state: "idle" });
  const syncing = useRef(false);
  const [newProject, setNewProject] = useState(false);
  const [newFolder, setNewFolder] = useState(false);
  const [newClassroom, setNewClassroom] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [limitHit, setLimitHit] = useState<LimitKind | "images" | null>(null);
  useEffect(() => void loadAppTexts(), []);

  useEffect(() => {
    const offs: (() => void)[] = [];
    let gone = false;
    const keep = (p: Promise<() => void>) => void p.then((f) => (gone ? f() : offs.push(f)));
    keep(watchMyProjects(user.uid, setAllProjects));
    let asked = false;
    keep(
      watchAccount(user.uid, (a) => {
        setAccount(a);
        // once per sign-in: ask again when not asked lately (a school licence given or taken, a PHP school coming over…)
        if (asked) return;
        asked = true;
        if (Date.now() - (a?.subscription?.checkedAt ?? 0) > 60 * 60 * 1000) void refreshSubscription().catch(() => undefined);
      }),
    );
    keep(watchShared(user.uid, setShared));
    keep(watchClassrooms(user.uid, setClassrooms));
    return () => {
      gone = true;
      offs.forEach((f) => f());
    };
  }, [user.uid]);

  const resync = () => {
    if (syncing.current) return;
    syncing.current = true;
    setSync({ state: "running" });
    syncMyProjects((progress) => setSync({ state: "running", progress }))
      .then((res) => setSync({ state: "done", progress: { total: res.total, done: res.total, failed: res.failed.length } }))
      .catch(() => setSync({ state: "error" }))
      .finally(() => {
        syncing.current = false;
      });
  };
  // Once per sign-in.
  useEffect(() => {
    resync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.uid]);

  // Close the drawer on navigation (phones).
  useEffect(() => setMenuOpen(false), [route]);

  const data: HomeData = { user, projects, binned, shared, classrooms, account, sync, resync };
  const folders = account?.folders ?? [];

  const limits: FreeLimits = {
    check: async (kind) => {
      // copies of the demos do not count (as in the app: ProjectsManager.AllowNewProjectsForFree)
      const have = kind === "projects" ? (projects ?? []).filter((p) => p.type !== "demoGame" && p.type !== "quizDemo").length : kind === "folders" ? folders.length : classrooms.length;
      if (canCreate(kind, have, account?.subscription)) return true;
      // not Premium as far as this page knows: ask again unless just asked (an app subscriber…)
      if (Date.now() - (account?.subscription?.checkedAt ?? 0) > 10 * 60 * 1000) {
        try {
          if (isPremium(await refreshSubscription())) return true;
        } catch {
          /* the alert, then */
        }
      }
      setLimitHit(kind);
      return false;
    },
    hit: (kind) => setLimitHit(kind),
  };
  /** A function error: the free-limit alert, or the caller's own handling. */
  const orLimit = (err: unknown) => {
    const kind = limitOfError(err);
    if (!kind) throw err;
    setLimitHit(kind);
  };

  return (
    <FreeLimitsContext.Provider value={limits}>
    <div className="happ" dir={uiDir(currentLanguage())}>
      <button className="happ-burger" onClick={() => setMenuOpen((o) => !o)} aria-label="Menu">
        <span />
        <span />
        <span />
      </button>
      <aside className={`happ-side ${menuOpen ? "open" : ""}`}>
        <a className="happ-logo" href="#/projects">
          <img src={libraryUrl("ui/logo.png")} alt="Make It" />
        </a>
        <nav>
          <h2>{t("projects")}</h2>
          <NavItem route={{ name: "projects", filter: "all" }} current={route} icon="projects" label={t("myProjects")} />
          <NavItem route={{ name: "shared" }} current={route} icon="shared" label={t("sharedWithMe")} />
          <NavItem route={{ name: "projects", filter: "game" }} current={route} icon="games" label={t("games")} />
          <NavItem route={{ name: "projects", filter: "quiz" }} current={route} icon="quizzes" label={t("quizzes")} />
          {/* HomeController.recentlyDeletedButton: only while something is in the bin */}
          {(binned?.length ?? 0) > 0 && <NavItem route={{ name: "bin" }} current={route} icon="delete" label={t("recentlyDeleted")} />}
          <button className="nav-item nav-action" onClick={() => void limits.check("projects").then((ok) => ok && setNewProject(true))}>
            <Icon name="add" />
            {t("newProject")}
          </button>

          <h2>{t("folders")}</h2>
          {folders.map((f) => (
            <NavItem key={f.id} route={{ name: "folder", id: f.id }} current={route} icon="folder" label={f.name} />
          ))}
          <button className="nav-item nav-action" onClick={() => void limits.check("folders").then((ok) => ok && setNewFolder(true))}>
            <Icon name="add" />
            {t("newFolder")}
          </button>

          <h2>{t("classrooms")}</h2>
          {classrooms.map((c) => (
            <NavItem key={c.id} route={{ name: "classroom", id: c.id }} current={route} icon="class" label={c.name} />
          ))}
          <button className="nav-item nav-action" onClick={() => void limits.check("classrooms").then((ok) => ok && setNewClassroom(true))}>
            <Icon name="add" />
            {t("newClassroom")}
          </button>

          <h2>{t("settings")}</h2>
          <NavItem route={{ name: "account" }} current={route} icon="account" label={t("account")} />
          <NavItem route={{ name: "subscription" }} current={route} icon="subscription" label={t("subscription")} />
          <NavItem route={{ name: "settings" }} current={route} icon="config" label={t("settings")} />
          <NavItem route={{ name: "help" }} current={route} icon="help" label={t("help")} />
        </nav>
        <div className="happ-sync">
          {sync.state === "running" && (sync.progress && sync.progress.total > 0 ? `${t("syncing")} ${Math.min(sync.progress.done, sync.progress.total)}/${sync.progress.total}` : `${t("syncing")}…`)}
          {sync.state === "error" && <span className="error">{t("errServer")}</span>}
        </div>
      </aside>
      {menuOpen && <div className="happ-veil" onClick={() => setMenuOpen(false)} />}
      <main className="happ-main">
        <Content route={route} data={data} onPlay={onPlay} />
      </main>

      {newProject && <NewProjectDialog onClose={() => setNewProject(false)} />}
      {newFolder && <PromptDialog title={t("newFolder")} label={t("folderName")} submitLabel={t("create")} onSubmit={async (name) => void (await createFolder(name).catch(orLimit))} onClose={() => setNewFolder(false)} />}
      {newClassroom && (
        <PromptDialog
          title={t("newClassroom")}
          label={t("classroomName")}
          submitLabel={t("create")}
          onSubmit={async (name) => {
            const r = await createClassroom(name).catch(orLimit);
            if (r) navigate({ name: "classroom", id: r.classroomId });
          }}
          onClose={() => setNewClassroom(false)}
        />
      )}
      {limitHit && (
        <AppAlert
          title={subText("defaultErrorTitle")}
          message={limitHit === "images" ? subText(LIMIT_MESSAGE.images) : `${subText(LIMIT_MESSAGE[limitHit])} ${subText("sNotSubscribedMessage")}`}
          okLabel={subText("mhSubscribe")}
          cancelLabel={t("cancel")}
          onOk={() => {
            setLimitHit(null);
            navigate({ name: "subscription" });
          }}
          onCancel={() => setLimitHit(null)}
        />
      )}
    </div>
    </FreeLimitsContext.Provider>
  );
}

function NavItem({ route, current, icon, label }: { route: Route; current: Route; icon: string; label: string }) {
  const on = sameRoute(route, current) || (route.name === "projects" && current.name === "project" && route.filter === "all");
  return (
    <a className={`nav-item ${on ? "on" : ""}`} href={hrefOf(route)}>
      <Icon name={icon} />
      <span className="nav-label">{label}</span>
    </a>
  );
}

function Content({ route, data, onPlay }: { route: Route; data: HomeData; onPlay: (s: ProjectSource) => void }) {
  switch (route.name) {
    case "projects":
      return <ProjectsView data={data} filter={route.filter} />;
    case "folder":
      return <FolderView data={data} folderId={route.id} />;
    case "project":
      return <ProjectPage data={data} projectId={route.id} onPlay={onPlay} />;
    case "shared":
      return <SharedView data={data} onPlay={onPlay} />;
    case "bin":
      return <BinView data={data} />;
    case "classroom":
      return <ClassroomPage classroomId={route.id} myProjects={data.projects ?? []} classrooms={data.classrooms} />;
    case "settings":
      return <SettingsView account={data.account} />;
    case "help":
      return <HelpView />;
    case "subscription":
      return <SubscriptionView user={data.user} account={data.account} />;
    case "school":
      return <SchoolView />;
    case "student":
      return <StudentView onPlay={onPlay} />;
    case "demos":
      // the Demos tab is gone: an account has the demos in My projects (or adds them when it has none)
      return <ProjectsView data={data} filter="all" />;
    case "account":
      return <AccountView data={data} />;
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Project lists
// ---------------------------------------------------------------------------

function sortProjects(list: ProjectSummary[], order: ProjectsOrder) {
  const byName = (a: ProjectSummary, b: ProjectSummary) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
  const cmp = { nameAsc: byName, nameDesc: (a: ProjectSummary, b: ProjectSummary) => byName(b, a), dateDesc: (a: ProjectSummary, b: ProjectSummary) => b.updated - a.updated, dateAsc: (a: ProjectSummary, b: ProjectSummary) => a.updated - b.updated }[order];
  return [...list].sort(cmp);
}

/**
 * The account's order of the projects (settings.projectsOrder, newest first until chosen), the same
 * on every device; a choice shows at once, before Firestore brings it back.
 */
function useProjectsOrder(data: HomeData): [ProjectsOrder, (o: ProjectsOrder) => void] {
  const saved = data.account?.settings?.projectsOrder ?? "dateDesc";
  const [mine, setMine] = useState<ProjectsOrder | null>(null);
  useEffect(() => setMine(null), [saved]);
  return [
    mine ?? saved,
    (o) => {
      setMine(o);
      void saveProjectsOrder(o).catch(() => setMine(null));
    },
  ];
}

function ProjectGrid({ list, empty }: { list: ProjectSummary[]; empty: ReactNode }) {
  if (list.length === 0) return <div className="empty">{empty}</div>;
  return (
    <div className="pgrid">
      {list.map((p) => (
        <a key={p.id} className="pcard" href={hrefOf({ name: "project", id: p.id })} title={p.name}>
          <span className="pcard-art" style={templateArt(p.template)}>
            <TypeBadge type={p.type} />
          </span>
          <span className="pcard-name">{p.name || "—"}</span>
        </a>
      ))}
    </div>
  );
}

/**
 * By name and by date, as the app's top bar (TopBarHome.OrderByName / OrderByDate): the one in use is
 * lit, and tapping it again turns the order round (A→Z / Z→A, newest / oldest first).
 */
function SortButtons({ order, onChange }: { order: ProjectsOrder; onChange: (o: ProjectsOrder) => void }) {
  const byName = order === "nameAsc" || order === "nameDesc";
  return (
    <div className="sort-btns" role="group" aria-label={t("sort")}>
      <button className={`sort-btn ${byName ? "on" : ""}`} onClick={() => onChange(order === "nameAsc" ? "nameDesc" : "nameAsc")} title={t("sortByName")} aria-pressed={byName}>
        {order === "nameDesc" ? "Z–A" : "A–Z"}
      </button>
      <button className={`sort-btn ${byName ? "" : "on"}`} onClick={() => onChange(order === "dateDesc" ? "dateAsc" : "dateDesc")} title={t("sortByDate")} aria-pressed={!byName}>
        <Icon name="timer" /> {order === "dateAsc" ? "↑" : "↓"}
      </button>
    </div>
  );
}

function ProjectsView({ data, filter }: { data: HomeData; filter: "all" | "game" | "quiz" }) {
  const [by, setBy] = useProjectsOrder(data);
  const [q, setQ] = useState("");
  const list = useMemo(() => {
    const all = data.projects ?? [];
    const f = filter === "all" ? all : all.filter((p) => (filter === "quiz") === isQuizType(p.type));
    return sortProjects(f.filter((p) => matchesSearch(p.name, q)), by);
  }, [data.projects, filter, by, q]);
  const title = filter === "game" ? t("games") : filter === "quiz" ? t("quizzes") : t("myProjects");
  return (
    <section className="view">
      <header className="view-head">
        <h1>{title}</h1>
        <div className="view-tools">
          <SearchBox value={q} onChange={setQ} />
          <SortButtons order={by} onChange={setBy} />
        </div>
      </header>
      {data.projects === null ? (
        <div className="empty muted">…</div>
      ) : (
        <ProjectGrid
          list={list}
          empty={
            q.trim() ? (
              <p>{t("noSearchResults")}</p>
            ) : (
              <>
                <h2>{t("noProjectsTitle")}</h2>
                <p>{data.sync.state === "running" ? `${t("syncing")}…` : t("noProjectsText")}</p>
                {data.sync.state !== "running" && (data.projects ?? []).length === 0 && <AddDemosButton />}
              </>
            )
          }
        />
      )}
    </section>
  );
}

/** An empty "My projects": copies of the demos, as a new account gets them (the Demos tab is gone). */
function AddDemosButton() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  return (
    <>
      <button
        className="btn btn-big add-demos"
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          setError(false);
          try {
            await addDemos(); // the list shows them as they arrive
          } catch {
            setError(true);
            setBusy(false);
          }
        }}
      >
        <Icon name="templates" /> {busy ? "…" : t("addDemos")}
      </button>
      {error && <p className="error">{t("errGeneric")}</p>}
    </>
  );
}

function FolderView({ data, folderId }: { data: HomeData; folderId: number }) {
  const folder = (data.account?.folders ?? []).find((f) => f.id === folderId);
  const [by, setBy] = useProjectsOrder(data);
  const [dialog, setDialog] = useState<"add" | "rename" | "delete" | null>(null);
  const [q, setQ] = useState("");
  const list = useMemo(() => {
    if (!folder) return [];
    const inFolder = new Set(folder.projects);
    return sortProjects((data.projects ?? []).filter((p) => inFolder.has(p.id) && matchesSearch(p.name, q)), by);
  }, [folder, data.projects, by, q]);
  if (!folder) return <section className="view">{data.account ? <div className="empty muted">{t("folderGone")}</div> : null}</section>;
  return (
    <section className="view">
      <header className="view-head">
        <h1>{folder.name}</h1>
        <div className="view-tools">
          <SearchBox value={q} onChange={setQ} />
          <SortButtons order={by} onChange={setBy} />
          <MoreMenu
            items={[
              { label: t("addProjects"), icon: "add", onClick: () => setDialog("add") },
              { label: t("renameFolder"), icon: "rename", onClick: () => setDialog("rename") },
              { label: t("deleteFolderMenu"), icon: "delete", danger: true, onClick: () => setDialog("delete") },
            ]}
          />
        </div>
      </header>
      {dialog === "add" && (
        <ProjectCardPicker
          title={`${t("addProjectsToFolder")} "${folder.name}"`}
          projects={sortProjects(data.projects ?? [], "dateDesc")}
          onDone={(ids) => (ids.length ? addProjectsToFolder(folder.id, ids) : Promise.resolve())}
          onClose={() => setDialog(null)}
        />
      )}
      <ProjectGrid
        list={list}
        empty={
          q.trim() ? (
            <p>{t("noSearchResults")}</p>
          ) : (
            <>
              <h2>{t("noProjectsTitle")}</h2>
              <p>{t("emptyFolderText")}</p>
            </>
          )
        }
      />
      {dialog === "rename" && <PromptDialog title={t("rename")} label={t("folderName")} initial={folder.name} onSubmit={async (name) => void (await renameFolder(folder.id, name))} onClose={() => setDialog(null)} />}
      {dialog === "delete" && (
        <ConfirmDialog
          title={`${t("delete")} "${folder.name}"`}
          text={`${t("deleteFolder")}\n${t("deleteFolderText")}`}
          confirmLabel={t("delete")}
          danger
          onConfirm={async () => {
            await deleteFolder(folder.id);
            navigate({ name: "projects", filter: "all" });
          }}
          onClose={() => setDialog(null)}
        />
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// New project
// ---------------------------------------------------------------------------

function NewProjectDialog({ onClose }: { onClose: () => void }) {
  const limits = useFreeLimits();
  const [name, setName] = useState("");
  const [type, setType] = useState<ProjectKind>("game");
  const [template, setTemplate] = useState("makeIt");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const create = async () => {
    const n = name.trim();
    if (!n || busy) return;
    setBusy(true);
    setError(null);
    try {
      const { projectId } = await createProject(n, type, template, currentLanguage());
      onClose();
      navigate({ name: "edit", id: projectId });
    } catch (err) {
      const kind = limitOfError(err);
      if (kind) {
        onClose();
        limits.hit(kind);
        return;
      }
      setError(t("errGeneric"));
      setBusy(false);
    }
  };
  return (
    <Modal title={t("newProject")} onClose={onClose} className="modal-wide">
      <div className="dialog-form">
        <input dir="auto" className="text-input" value={name} onChange={(e) => setName(e.target.value)} placeholder={t("projectName")} aria-label={t("projectName")} maxLength={100} autoFocus />
        <div className="seg">
          <button className={type === "game" ? "on" : ""} onClick={() => setType("game")}>
            <Icon name="games" /> {t("game")}
          </button>
          <button className={type === "quiz" ? "on" : ""} onClick={() => setType("quiz")}>
            <Icon name="quizzes" /> {t("quiz")}
          </button>
        </div>
        <h3>{t("template")}</h3>
        <div className="tpl-grid">
          {TEMPLATE_CHOICES.map((tp) => (
            <button key={tp} className={`tpl ${template === tp ? "on" : ""}`} style={templateArt(tp)} onClick={() => setTemplate(tp)} title={tp} aria-label={tp} aria-pressed={template === tp} />
          ))}
          {COLOR_CHOICES.map((c) => (
            <button key={c} className={`tpl ${template === c ? "on" : ""}`} style={{ backgroundColor: c }} onClick={() => setTemplate(c)} title={c} aria-label={c} aria-pressed={template === c} />
          ))}
        </div>
        {error && <p className="error">{error}</p>}
        <div className="dialog-actions">
          <button className="btn btn-secondary" onClick={onClose}>
            {t("cancel")}
          </button>
          <button className="btn" onClick={() => void create()} disabled={!name.trim() || busy}>
            {busy ? "…" : t("create")}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Project page
// ---------------------------------------------------------------------------

function ProjectPage({ data, projectId, onPlay }: { data: HomeData; projectId: string; onPlay: (s: ProjectSource) => void }) {
  const [project, setProject] = useState<Project | null | undefined>(undefined);
  const [meta, setMeta] = useState<ProjectMeta | null>(null);
  const [tab, setTab] = useState<"preview" | "share" | "ranking" | "stats" | "classrooms" | "mine" | "public">("preview");
  const [dialog, setDialog] = useState<"rename" | "delete" | "folder" | null>(null);
  const [shareBusy, setShareBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const summary = data.projects?.find((p) => p.id === projectId);
  const sharedEntry = data.shared?.find((p) => p.id === projectId);

  useEffect(() => {
    let off: (() => void) | undefined;
    let gone = false;
    setProject(undefined);
    void watchProject(projectId, (p, m) => {
      if (gone) return;
      setProject(p);
      setMeta(m);
    }).then((f) => (gone ? f() : (off = f)));
    return () => {
      gone = true;
      off?.();
    };
  }, [projectId]);

  if (project === undefined) return <section className="view" />;
  const binnedEntry = data.binned?.find((p) => p.id === projectId);
  if (project && binnedEntry) {
    return (
      <section className="view project-page">
        <header className="pp-top">
          <a className="back" href={hrefOf({ name: "bin" })}>
            ‹ {t("recentlyDeleted")}
          </a>
          <h1>{project.name}</h1>
          <span />
        </header>
        <div className="pp-hero" style={templateBanner(project.template)} />
        <BinnedActions project={binnedEntry} layout="page" />
      </section>
    );
  }
  if (project === null) {
    return (
      <section className="view">
        <a className="back" href="#/projects">
          ‹ {t("myProjects")}
        </a>
        <div className="empty muted">{t("errNotFound")}</div>
      </section>
    );
  }

  const lang = currentLanguage();
  const owned = meta?.ownerUid === data.user.uid;
  // HomeReportsNotMineSubpanel: a quiz someone shared with me, as played in this browser
  const played = !owned && isQuizType(project.type) ? loadLocalAnswers(projectId) : null;
  const code = summary?.shareCode ?? (project.sharing.shareCode > 0 ? String(project.sharing.shareCode) : null);
  // the Unity app's share link format (HomeSharePanel.cs), see site.ts
  const playUrl = code ? playLink(code) : playProjectLink(projectId);
  const screenshots = new Set(meta?.screenshots ?? []);
  const bust = summary?.updated ?? 0;
  const folders = data.account?.folders ?? [];

  const limits = useFreeLimits();
  /** ActionsPanel "clone": a copy is one more project (ProjectsManager.AllowNewProjectsForFree). */
  const duplicate = async () => {
    if (!(await limits.check("projects"))) return;
    try {
      const r = await duplicateProject(projectId);
      navigate({ name: "project", id: r.projectId });
    } catch (err) {
      const kind = limitOfError(err);
      if (kind) limits.hit(kind);
    }
  };
  const getCode = async () => {
    if (shareBusy) return;
    setShareBusy(true);
    try {
      await shareProject(projectId);
    } finally {
      setShareBusy(false);
    }
  };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(playUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignore */
    }
  };

  return (
    <section className="view project-page">
      <header className="pp-top">
        <a className="back" href={owned ? "#/projects" : "#/shared"}>
          ‹ {owned ? t("myProjects") : t("sharedWithMe")}
        </a>
        <h1>{project.name}</h1>
        {owned ? (
          <MoreMenu
            items={[
              { label: t("rename"), icon: "rename", onClick: () => setDialog("rename") },
              { label: t("duplicate"), icon: "clone", onClick: () => void duplicate() },
              { label: t("moveToFolders"), icon: "folder", onClick: () => setDialog("folder") },
              { label: t("delete"), icon: "delete", danger: true, onClick: () => setDialog("delete") },
            ]}
          />
        ) : sharedEntry ? (
          <MoreMenu
            items={[
              // ActionsPanel "clone": a project shared with you, when its owner allows cloning
              ...(project.sharing.allowCloning ? [{ label: t("duplicate"), icon: "clone", onClick: () => void duplicate() }] : []),
              { label: t("removeShared"), icon: "delete", danger: true, onClick: () => void forgetShared(projectId).then(() => navigate({ name: "shared" })) },
            ]}
          />
        ) : (
          <span />
        )}
      </header>
      <div className="pp-hero" style={templateBanner(project.template)}>
        {owned && (
          <button className="btn btn-big" onClick={() => navigate({ name: "edit", id: projectId })}>
            {t("edit")}
          </button>
        )}
        <button className="btn btn-big" onClick={() => onPlay({ kind: "project", projectId })}>
          {t("play")}
        </button>
      </div>
      <div className="pp-strip">
        <Icon name="pages" />
        {project.slides.length === 1 ? t("pagesCountOne") : `${project.slides.length} ${t("pages")}`}
      </div>
      <div className="tabs">
        <button className={tab === "preview" ? "on" : ""} onClick={() => setTab("preview")}>
          {t("preview")}
        </button>
        {played && (
          <>
            <button className={tab === "mine" ? "on" : ""} onClick={() => setTab("mine")}>
              {t("myResults")}
            </button>
            <button className={tab === "public" ? "on" : ""} onClick={() => setTab("public")}>
              {t("ranking")}
            </button>
          </>
        )}
        {owned && (
          <button className={tab === "share" ? "on" : ""} onClick={() => setTab("share")}>
            {t("share")}
          </button>
        )}
        {owned && isQuizType(project.type) && (
          <>
            <button className={tab === "ranking" ? "on" : ""} onClick={() => setTab("ranking")}>
              {t("ranking")}
            </button>
            <button className={tab === "stats" ? "on" : ""} onClick={() => setTab("stats")}>
              {t("statistics")}
            </button>
          </>
        )}
        {owned && data.classrooms.length > 0 && (
          <button className={tab === "classrooms" ? "on" : ""} onClick={() => setTab("classrooms")}>
            {t("classrooms")}
          </button>
        )}
      </div>
      {tab === "classrooms" && <ProjectClassrooms project={project} classrooms={data.classrooms} />}
      {tab === "ranking" && <RankingReport project={project} />}
      {tab === "stats" && <StatsReport project={project} />}
      {tab === "mine" && played && <MyAnswersReport project={project} played={played} />}
      {tab === "public" && played && <RankingReport project={project} played={played} />}

      {tab === "preview" && (
        <div className="slides">
          {project.slides.length === 0 && (
            <div className="empty">
              <h2>{t("noPagesTitle")}</h2>
              {owned && <p>{t("noPagesText")}</p>}
            </div>
          )}
          {project.slides.map((s, i) => (
            <div key={s.id} className="slide-row">
              <span className="slide-num">{i + 1}</span>
              <span className="slide-shot">
                {screenshots.has(s.id) ? (
                  <img src={screenshotUrl(projectId, s.id, bust)} alt="" loading="lazy" />
                ) : (
                  <Suspense fallback={null}>
                    <LivePreview project={project} slide={s} />
                  </Suspense>
                )}
              </span>
              <span className="slide-name">{slideTypeName(s.type, lang)}</span>
            </div>
          ))}
        </div>
      )}
      {tab === "share" && (
        <div className="share-pane">
          {code ? (
            <>
              <p className="muted">{t("shareText")}</p>
              <div className="share-code">{code}</div>
              <div className="share-link">
                <input className="text-input" readOnly value={playUrl} onFocus={(e) => e.currentTarget.select()} />
                <button className="btn" onClick={() => void copy()}>
                  {copied ? t("copied") : t("copyLink")}
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="muted">{t("noCodeText")}</p>
              <button className="btn btn-big" onClick={() => void getCode()} disabled={shareBusy}>
                {shareBusy ? "…" : t("getCode")}
              </button>
            </>
          )}
        </div>
      )}

      {dialog === "rename" && <PromptDialog title={t("rename")} label={t("projectName")} initial={project.name} onSubmit={async (name) => void (await renameProject(projectId, name))} onClose={() => setDialog(null)} />}
      {dialog === "delete" && (
        <ConfirmDialog
          title={`${t("delete")} "${project.name}"`}
          text={`${t("deleteProject")}\n${t("deleteProjectText")}`}
          confirmLabel={t("delete")}
          danger
          onConfirm={async () => {
            await deleteProject(projectId);
            navigate({ name: "projects", filter: "all" });
          }}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog === "folder" && <FoldersPicker projectId={projectId} folders={folders} onClose={() => setDialog(null)} />}
    </section>
  );
}

/**
 * AddToFoldersPanel ("Move Project to"): every folder with a tick; a project can
 * be in several folders. "New Folder" at the end creates one, already ticked.
 * Done (OKButton) leaves the project in exactly the ticked folders.
 */
function FoldersPicker({ projectId, folders, onClose }: { projectId: string; folders: Folder[]; onClose: () => void }) {
  const limits = useFreeLimits();
  const [list, setList] = useState(folders);
  const [ticked, setTicked] = useState(() => new Set(folders.filter((f) => f.projects.includes(projectId)).map((f) => f.id)));
  const [naming, setNaming] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toggle = (id: number) =>
    setTicked((cur) => {
      const next = new Set(cur);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  const create = async (e: FormEvent) => {
    e.preventDefault();
    const name = (naming ?? "").trim();
    if (!name || busy) return;
    if (list.some((f) => f.name.trim().toLowerCase() === name.toLowerCase())) return setError(t("newFolderNameExists"));
    setBusy(true);
    setError(null);
    try {
      const r = await createFolder(name);
      const known = new Set(list.map((f) => f.id));
      const added = r.folders.filter((f) => !known.has(f.id));
      setList(r.folders);
      setTicked((cur) => new Set([...cur, ...added.map((f) => f.id)]));
      setNaming(null);
    } catch (err) {
      const kind = limitOfError(err);
      if (kind) {
        setNaming(null);
        limits.hit(kind);
      } else setError(t("errTryAgain"));
    } finally {
      setBusy(false);
    }
  };
  const done = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await setProjectFolders(projectId, [...ticked]);
      onClose();
    } catch {
      setError(t("errTryAgain"));
      setBusy(false);
    }
  };
  return (
    <Modal title={t("addToFolders")} onClose={busy ? () => undefined : onClose} className="folders-picker">
      <ul className="pick-list tick-list">
        {list.map((f) => (
          <li key={f.id}>
            <button className={ticked.has(f.id) ? "on" : ""} onClick={() => toggle(f.id)} aria-pressed={ticked.has(f.id)} disabled={busy}>
              <Icon name="folder" />
              <span className="tick-name">{f.name}</span>
              <span className="tick" aria-hidden>
                {ticked.has(f.id) && <Icon name="tick" />}
              </span>
            </button>
          </li>
        ))}
        <li>
          {naming === null ? (
            <button className="new-row" onClick={() => void limits.check("folders").then((ok) => ok && (setNaming(""), setError(null)))} disabled={busy}>
              <Icon name="add" />
              <span className="tick-name">{t("newFolder")}</span>
            </button>
          ) : (
            <form className="new-folder-form" onSubmit={create}>
              <input dir="auto" className="text-input" autoFocus placeholder={t("folderName")} aria-label={t("folderName")} value={naming} maxLength={60} onChange={(e) => setNaming(e.target.value)} onKeyDown={(e) => e.key === "Escape" && (e.stopPropagation(), setNaming(null))} />
              <button type="submit" className="btn" disabled={!naming.trim() || busy}>
                {t("create")}
              </button>
            </form>
          )}
        </li>
      </ul>
      {list.length === 0 && naming === null && <p className="muted">{t("noFoldersYet")}</p>}
      {error && <p className="error">{error}</p>}
      <div className="dialog-actions">
        <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>
          {t("cancel")}
        </button>
        <button type="button" className="btn" onClick={() => void done()} disabled={busy || naming !== null}>
          {busy ? "…" : t("done")}
        </button>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Recently Deleted (HomeMode.recentlyDeleted)
// ---------------------------------------------------------------------------

/** "N days" left in the bin (IconsProjects.daysLeft). */
function daysText(p: ProjectSummary): string {
  const n = binDaysLeft(p.deletedAt ?? Date.now());
  return n === 1 ? t("binDay") : t("binDays").replace("{n}", String(n));
}

function BinView({ data }: { data: HomeData }) {
  const [picked, setPicked] = useState<ProjectSummary | null>(null);
  // most recently deleted first
  const list = useMemo(() => [...(data.binned ?? [])].sort((a, b) => (b.deletedAt ?? 0) - (a.deletedAt ?? 0)), [data.binned]);
  return (
    <section className="view">
      <header className="view-head">
        <h1>{t("recentlyDeleted")}</h1>
      </header>
      {data.binned === null ? (
        <div className="empty muted">…</div>
      ) : list.length === 0 ? (
        <div className="empty">
          <h2>{t("noProjectsTitle")}</h2>
        </div>
      ) : (
        <>
          <div className="pgrid">
            {list.map((p) => (
              <button key={p.id} className="pcard pcard-binned" onClick={() => setPicked(p)} title={p.name}>
                <span className="pcard-art" style={templateArt(p.template)}>
                  <TypeBadge type={p.type} />
                  <span className="pcard-days">{daysText(p)}</span>
                </span>
                <span className="pcard-name">{p.name || "—"}</span>
              </button>
            ))}
          </div>
          {/* TextRecentlyDeleted */}
          <p className="muted bin-info">{t("recentlyDeletedInfo")}</p>
        </>
      )}
      {picked && (
        <Modal title={picked.name || "—"} onClose={() => setPicked(null)}>
          <BinnedActions project={picked} layout="sheet" onDone={() => setPicked(null)} />
        </Modal>
      )}
    </section>
  );
}

/**
 * What can be done with a project in the bin (ActionsPanel in recentlyDeleted
 * mode): Recover Project, or Delete Permanently after the app's alert
 * (AlertsController.DeleteProjectForever).
 */
function BinnedActions({ project, layout, onDone }: { project: ProjectSummary; layout: "sheet" | "page"; onDone?: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const [purge, setPurge] = useState(false);
  const recover = async () => {
    if (busy) return;
    setBusy(true);
    setError(false);
    try {
      await restoreProject(project.id);
      onDone?.();
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  };
  const alert = purge && (
    <ConfirmDialog
      title={`${t("delete")} "${project.name}"`}
      text={t("deleteForeverText")}
      confirmLabel={t("deleteForever")}
      onConfirm={async () => {
        await purgeProject(project.id);
        onDone?.();
        if (layout === "page") navigate({ name: "bin" });
      }}
      onClose={() => setPurge(false)}
    />
  );
  if (layout === "page") {
    return (
      <div className="bin-notice">
        <p>{t("inBinText").replace("{days}", daysText(project))}</p>
        {error && <p className="error">{t("errGeneric")}</p>}
        <div className="dialog-actions">
          <button className="btn btn-secondary" onClick={() => setPurge(true)} disabled={busy}>
            {t("deleteForever")}
          </button>
          <button className="btn" onClick={() => void recover()} disabled={busy}>
            {busy ? "…" : t("recover")}
          </button>
        </div>
        {alert}
      </div>
    );
  }
  return (
    <>
      <p className="muted bin-days">{t("inBinText").replace("{days}", daysText(project))}</p>
      <ul className="pick-list bin-actions">
        <li>
          <button onClick={() => void recover()} disabled={busy}>
            <Icon name="recover" /> {busy ? "…" : t("recover")}
          </button>
        </li>
        <li>
          <button className="danger" onClick={() => setPurge(true)} disabled={busy}>
            <Icon name="delete" /> {t("deleteForever")}
          </button>
        </li>
      </ul>
      {error && <p className="error">{t("errGeneric")}</p>}
      {alert}
    </>
  );
}

// ---------------------------------------------------------------------------
// Shared with me, account
// ---------------------------------------------------------------------------

function SharedView({ data }: { data: HomeData; onPlay: (s: ProjectSource) => void }) {
  const [redeem, setRedeem] = useState(false);
  const [q, setQ] = useState("");
  const all = data.shared ?? [];
  const list = all.filter((p) => matchesSearch(p.name, q));
  return (
    <section className="view">
      <header className="view-head">
        <h1>{t("sharedWithMe")}</h1>
        {all.length > 0 && (
          <div className="view-tools">
            <SearchBox value={q} onChange={setQ} />
          </div>
        )}
      </header>
      <button className="redeem-card" onClick={() => setRedeem(true)}>
        <img src={libraryUrl("ui/redeem.png")} alt="" />
        <span>{t("redeemCode")}</span>
      </button>
      {data.shared !== null && list.length === 0 ? (
        <div className="empty">
          {q.trim() && all.length ? (
            <p>{t("noSearchResults")}</p>
          ) : (
            <>
              <h2>{t("noProjectsTitle")}</h2>
              <p>{t("noSharedText")}</p>
            </>
          )}
        </div>
      ) : (
        <div className="pgrid">
          {list.map((p) => (
            <a key={p.id} className="pcard" href={hrefOf({ name: "project", id: p.id })} title={p.name}>
              <span className="pcard-art" style={templateArt(p.template)}>
                <TypeBadge type={p.type} />
              </span>
              <span className="pcard-name">{p.name || "—"}</span>
            </a>
          ))}
        </div>
      )}
      {redeem && (
        <PromptDialog
          title={t("redeemCode")}
          label={t("code")}
          submitLabel={t("redeemCode")}
          onSubmit={async (code) => {
            const r = await redeemCode(code);
            navigate({ name: "project", id: r.projectId });
          }}
          onClose={() => setRedeem(false)}
        />
      )}
      <p className="muted small">{t("redeemPlayHint")}</p>
    </section>
  );
}

function AccountView({ data }: { data: HomeData }) {
  const [dialog, setDialog] = useState<"name" | "password" | "delete" | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  return (
    <section className="view">
      <h1>{t("account")}</h1>
      {notice && (
        <p className="account-notice" role="status">
          {notice}
        </p>
      )}
      <div className="panel-list">
        <button className="panel-row" onClick={() => setDialog("name")} title={t("changeName")}>
          <span>{t("name")}</span>
          <span className="muted row-value">
            {data.user.displayName || "—"}
            <Icon name="rename" size={18} />
          </span>
        </button>
        <div className="panel-row">
          <span>{t("email")}</span>
          <span className="muted">{data.user.email}</span>
        </div>
        <div className="panel-row">
          <span>{t("projects")}</span>
          <span className="muted">{data.projects?.length ?? "…"}</span>
        </div>
        <a className="panel-row" href="#/subscription">
          <span>{t("subscription")}</span>
          <span className="muted row-value">
            {isPremium(data.account?.subscription) ? subText("accSubscribed") : subText("accNotSubscribed")}
            <Icon name="link" size={14} />
          </span>
        </a>
        {managesSchool(data.account?.subscription) && (
          // AccountPanel.LoadSchoolStuff: the manager's "Manage Teachers"
          <a className="panel-row" href="#/school">
            <span>{st("manage")}</span>
            <span className="muted row-value">
              <span dir="auto">{data.account?.subscription?.school?.name}</span>
              <Icon name="link" size={14} />
            </span>
          </a>
        )}
      </div>
      <div className="panel-list">
        <button className="panel-row link" onClick={() => data.resync()} disabled={data.sync.state === "running"}>
          {t("refresh")}
        </button>
        <button className="panel-row link" onClick={() => void signOut()}>
          {t("logout")}
        </button>
      </div>
      <h2 className="account-h2">{t("security")}</h2>
      <div className="panel-list">
        {data.user.hasPassword && (
          <button className="panel-row link" onClick={() => setDialog("password")}>
            {t("changePassword")}
          </button>
        )}
        <a className="panel-row link" href="/privacy-policy" target="_blank" rel="noreferrer">
          {t("privacyPolicy")}
        </a>
        <button className="panel-row link danger" onClick={() => setDialog("delete")}>
          {t("deleteAccount")}
        </button>
      </div>
      {dialog === "name" && (
        <PromptDialog title={t("changeName")} label={t("name")} initial={data.user.displayName ?? ""} onSubmit={(name) => changeName(name)} onClose={() => setDialog(null)} />
      )}
      {dialog === "password" && (
        <PasswordDialog
          onDone={() => {
            setDialog(null);
            setNotice(t("passwordChanged"));
          }}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog === "delete" && <DeleteAccountDialog apple={!data.user.hasPassword} syncing={data.sync.state === "running"} onClose={() => setDialog(null)} />}
    </section>
  );
}

function authMessage(err: unknown): string {
  const code = err instanceof AuthError ? err.code : "unknown";
  switch (code) {
    case "wrong-password":
      return t("wrongPassword");
    case "weak-password":
      return t("errWeakPassword");
    case "too-many":
      return t("errTooMany");
    default:
      return t("errTryAgain");
  }
}

/** Change password: the current one (Firebase asks for a recent sign-in), then the new one twice (the app's accNewPassword / accRepeatPassword). */
function PasswordDialog({ onDone, onClose }: { onDone: () => void; onClose: () => void }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [again, setAgain] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (next !== again) return setError(t("passwordsDontMatch"));
    if (next.length < 6) return setError(t("errWeakPassword"));
    setBusy(true);
    setError(null);
    try {
      await changePassword(current, next);
      onDone();
    } catch (err) {
      setError(authMessage(err));
      setBusy(false);
    }
  };
  return (
    <Modal title={t("changePassword")} onClose={busy ? () => undefined : onClose}>
      <form onSubmit={submit} className="dialog-form">
        <input className="text-input" type="password" autoComplete="current-password" placeholder={t("currentPassword")} aria-label={t("currentPassword")} value={current} onChange={(e) => setCurrent(e.target.value)} autoFocus />
        <input className="text-input" type="password" autoComplete="new-password" placeholder={t("newPassword")} aria-label={t("newPassword")} value={next} onChange={(e) => setNext(e.target.value)} />
        <input className="text-input" type="password" autoComplete="new-password" placeholder={t("repeatPassword")} aria-label={t("repeatPassword")} value={again} onChange={(e) => setAgain(e.target.value)} />
        {error && <p className="error">{error}</p>}
        <div className="dialog-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>
            {t("cancel")}
          </button>
          <button type="submit" className="btn" disabled={!current || !next || !again || busy}>
            {busy ? "…" : t("save")}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * Delete the account, for good: what goes, the store subscription, and the
 * password again (an account without one, made with Apple, signs in with Apple again).
 */
function DeleteAccountDialog({ apple, syncing, onClose }: { apple: boolean; syncing: boolean; onClose: () => void }) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const remove = async (confirm: Parameters<typeof deleteAccount>[0]) => {
    setBusy(true);
    setError(null);
    try {
      await deleteAccount(confirm);
      try {
        sessionStorage.setItem("makeit.accountDeleted", "1");
      } catch {
        /* no storage: no notice */
      }
    } catch (err) {
      if (!(err instanceof AuthError && err.code === "cancelled")) setError(authMessage(err));
      setBusy(false);
    }
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (busy || !password || syncing) return;
    void remove({ password });
  };
  return (
    <Modal title={t("deleteAccountTitle")} onClose={busy ? () => undefined : onClose} className="delete-account">
      <form onSubmit={submit} className="dialog-form">
        <p>{t("deleteAccountText")}</p>
        <p className="muted small">{t("deleteAccountSubs")}</p>
        {!apple && (
          <>
            <label className="muted small" htmlFor="del-pass">
              {t("deleteAccountPassword")}
            </label>
            <input id="del-pass" className="text-input" type="password" autoComplete="current-password" placeholder={t("password")} value={password} onChange={(e) => setPassword(e.target.value)} disabled={busy} autoFocus />
          </>
        )}
        {error && <p className="error">{error}</p>}
        {busy && <p className="muted small">{t("deleteAccountBusy")}</p>}
        {syncing && !busy && <p className="muted small">{t("syncing")}…</p>}
        <div className="dialog-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>
            {t("cancel")}
          </button>
          {apple ? (
            <AppleButton onClick={() => void remove({ apple: true })} disabled={busy || syncing} />
          ) : (
            <button type="submit" className="btn btn-danger" disabled={!password || busy || syncing}>
              {busy ? "…" : t("deleteAccount")}
            </button>
          )}
        </div>
      </form>
    </Modal>
  );
}
